'use client';

import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TennisBallIcon, getBallLevelColor } from '@/components/ui/tennis-ball-icon';
import { PlayerAvatar } from '@/components/shared/player-avatar';
import { getLevelBadgeColor } from '@/components/shared/skill-level-utils';
import type { Player } from '@/shared/types/coach.types';

interface PlayerCardProps {
  player: Player;
  onClick?: () => void;
  onQuickEval?: () => void;
}

export function PlayerCard({ player, onClick, onQuickEval }: PlayerCardProps) {
  const fullName = `${player.firstName} ${player.lastName}`;
  const age = player.dateOfBirth
    ? Math.floor((Date.now() - new Date(player.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div
      onClick={onClick}
      className="bg-card rounded-lg shadow p-4 hover:shadow-lg transition cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <PlayerAvatar firstName={player.firstName} lastName={player.lastName} size="lg" />
          <div>
            <h3 className="font-bold text-lg text-foreground">{fullName}</h3>
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">
              {age !== null && <span>Age {age}</span>}
              {age !== null && (player.skillLevel || player.ballLevel) && <span>•</span>}
              <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', getLevelBadgeColor(player.skillLevel))}>
                {player.skillLevel || 'Unassigned'}
              </span>
              {(() => {
                const ball = getBallLevelColor(player.ballLevel);
                return ball ? (
                  <TennisBallIcon color={ball.color} title={`${ball.label} Ball`} />
                ) : null;
              })()}
            </div>
            <div className="flex gap-4 mt-2 text-sm">
              {player.utrRating && (
                <span className="text-primary font-semibold">UTR: {player.utrRating}</span>
              )}
              <span className="text-muted-foreground">Attendance: {player.attendance ?? 0}%</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickEval?.();
            }}
            className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
          >
            Quick Eval
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
